import type { CadMode } from '@/contexts/HomepageContext';
import { Sparkles, Wrench } from '@/components/ui/icons';
import { cn } from '@/lib/utils';

interface CadModeToggleProps {
  cadMode: CadMode;
  setCadMode: (mode: CadMode) => void;
}

// Visible labels are short (fits alongside NavButtons within the pill-width
// column — see TopToolbar's right-alignment); the full mode name still shows
// in the title tooltip.
const OPTIONS = [
  {
    value: 'automatic' as const,
    label: 'Auto',
    fullLabel: 'Automatic',
    Icon: Sparkles,
  },
  {
    value: 'manual' as const,
    label: 'Manual',
    fullLabel: 'Manual',
    Icon: Wrench,
  },
];

/**
 * Segmented Automatic | Manual control, styled to match the Venom dataroom's
 * Dark|Light toggle: one rounded bordered box, each option an icon+label, the
 * active option at full opacity with an inset ring, the inactive one dimmed.
 * Kept compact (h-6) so it fits the toolbar nav row WITHOUT growing it — the
 * pill row below must not move. See docs/freecad-manual-cad-integration-plan.md.
 */
export function CadModeToggle({ cadMode, setCadMode }: CadModeToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="CAD mode"
      // Outer radius = inner button radius (`rounded` = 4px) + border (1px) +
      // padding (p-0.5 = 2px) = 7px, so the two corners are concentric instead
      // of the outer radius reading tighter than the inner buttons'.
      className="flex h-6 items-center gap-0.5 rounded-[7px] border border-[#737373] bg-telekinesis-bg-dark p-0.5 text-[11px] leading-none"
    >
      {OPTIONS.map(({ value, label, fullLabel, Icon }) => {
        const active = cadMode === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setCadMode(value)}
            title={`${fullLabel} CAD`}
            className={cn(
              'flex items-center gap-1 rounded bg-telekinesis-bg-dark px-2 py-0.5 text-telekinesis-neutral-50 transition',
              active
                ? 'bg-[#0f3d24] text-[#107d40] opacity-100 ring-1 ring-inset ring-[#107d40]'
                : 'opacity-50 hover:opacity-80',
            )}
          >
            <Icon className="h-3 w-3" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
